import React, { useEffect, useState, useRef } from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import {
  ChevronRight,
  Twitter,
  Linkedin,
  Clock,
  Calendar,
  ArrowUp,
  Copy,
  Check,
} from "lucide-react";
import Footer from "../components/Footer";

const VercelBlog = () => {
  const [showBackToTop, setShowBackToTop] = useState(false);
  const [copied, setCopied] = useState(false);
  const [activeSection, setActiveSection] = useState("");

  const contentRef = useRef(null);

  // NEW: State and Refs for controlling the FIXED sidebar behavior
  const [tocState, setTocState] = useState("normal"); // 'normal' | 'fixed' | 'bottom'
  const [sidebarWidth, setSidebarWidth] = useState(0);
  const mainGridRef = useRef(null);
  const sidebarContainerRef = useRef(null);

  // Exact data from your prompt, beautifully formatted
  const articleSections = [
    {
      id: "section1",
      title: "The Era of 'Inherited' Access",
      content: `
        <p>In April 2026, Vercel, the backbone of modern web deployment, disclosed a significant security breach. To the casual observer, it was another headline. To the seasoned security professional, it was a canary in the coal mine for a new class of threat: <strong>The AI Supply Chain Attack</strong>.</p>
        <p>This breach was not characterized by the usual suspects. There were no zero-day exploits, no firewall bypasses, and no infrastructure failures. Instead, the attackers entered through the front door using the one thing modern enterprises have in abundance but manage poorly: Trust.</p>
      `,
    },
    {
      id: "section2",
      title: "Anatomy of an Identity Pivot",
      content: `
        <p>The attack chain was elegant in its simplicity and devastating in its velocity. It serves as a blueprint for the "AI-accelerated" tradecraft we are beginning to see in 2026.</p>
        <ul>
          <li><strong>The Entry Point:</strong> A third-party AI productivity tool, Context.ai, was compromised (reportedly via infostealer malware on a developer workstation).</li>
          <li><strong>The Integration Bridge:</strong> A Vercel employee had previously granted this tool broad OAuth permissions to their corporate Google Workspace.</li>
          <li><strong>The Silent Hijack:</strong> Using stolen OAuth refresh tokens, the attackers bypassed MFA and inherited the employee’s legitimate identity.</li>
          <li><strong>Lateral Maneuvering:</strong> From a Workspace foothold, the attackers pivoted into Vercel’s internal systems, enumerating environment variables and accessing internal data.</li>
        </ul>
        <p class="highlight-box"><strong>The Critical Distinction:</strong> The attacker didn’t need to exploit a vulnerability in Vercel’s code. They simply exploited the excessive permissions granted to an integration.</p>
        
       <div class="my-12 rounded-3xl overflow-hidden shadow-xl border border-slate-100 bg-slate-50 p-3">
  <img 
    src="https://res.cloudinary.com/dpn3bkjba/image/upload/v1777031682/Screenshot_2026-04-24_at_4.00.59_PM_1_uvtbhq.png" 
    alt="Attack Vector Visualization" 
    class="w-full h-auto object-contain rounded-2xl opacity-95 hover:opacity-100 transition-opacity duration-500"
  />
  <p class="text-center text-[10px] uppercase tracking-widest text-slate-400 mt-3 mb-1 font-bold">
    Visualization of Identity Pivot
  </p>
</div>
      `,
    },
    {
      id: "section3",
      title: "The Paradigm Shift: Identity is the New Perimeter",
      content: `
        <p>Historically, security models were built on the assumption that the network was the perimeter. We protected the "castle" with firewalls and gated the "keep" with MFA. The Vercel incident proves this model is officially obsolete.</p>
        <p>In this new reality:</p>
        <ul>
          <li><strong>Authentication ≠ Authorization:</strong> Valid login credentials are no longer enough when a third-party token can act on your behalf indefinitely.</li>
          <li><strong>Integrations are the Attack Surface:</strong> Every "Connect with Google" or "Install App" click is a potential permanent tunnel into your infrastructure.</li>
          <li><strong>Tokens are the New Credentials:</strong> Stolen tokens are often more valuable than passwords because they bypass traditional MFA triggers.</li>
        </ul>
      `,
    },
    {
      id: "section4",
      title: "Systemic Failures: Why Traditional SASE Isn't Enough",
      content: `
        <p>The root cause of this breach wasn't human error alone; it was a failure of governance over the AI-to-SaaS ecosystem.</p>
        
        <div class="overflow-x-auto mt-10 mb-10 rounded-2xl border border-slate-200 shadow-sm">
          <table class="min-w-full text-left border-collapse bg-white">
            <thead class="bg-slate-50">
              <tr>
                <th class="px-6 py-5 font-black text-slate-800 text-[11px] uppercase tracking-widest border-b border-slate-200">Failure Point</th>
                <th class="px-6 py-5 font-black text-slate-800 text-[11px] uppercase tracking-widest border-b border-slate-200">Traditional Reality</th>
                <th class="px-6 py-5 font-black text-[#00a684] text-[11px] uppercase tracking-widest border-b border-slate-200 bg-[#00a684]/5">The Modern Requirement</th>
              </tr>
            </thead>
            <tbody class="text-slate-600 divide-y divide-slate-100 text-sm">
              <tr class="hover:bg-slate-50 transition-colors">
                <td class="px-6 py-5 font-bold text-slate-800 border-r border-slate-50">OAuth Scopes</td>
                <td class="px-6 py-5 border-r border-slate-50">"Allow All" is common for convenience.</td>
                <td class="px-6 py-5 text-[#00a684] font-semibold bg-[#00a684]/5">Granular, time-bound, and audited scopes.</td>
              </tr>
              <tr class="hover:bg-slate-50 transition-colors">
                <td class="px-6 py-5 font-bold text-slate-800 border-r border-slate-50">Verification</td>
                <td class="px-6 py-5 border-r border-slate-50">Periodic or login-based MFA.</td>
                <td class="px-6 py-5 text-[#00a684] font-semibold bg-[#00a684]/5">Continuous, context-aware session validation.</td>
              </tr>
              <tr class="hover:bg-slate-50 transition-colors">
                <td class="px-6 py-5 font-bold text-slate-800 border-r border-slate-50">AI Governance</td>
                <td class="px-6 py-5 border-r border-slate-50">Treated as standard SaaS.</td>
                <td class="px-6 py-5 text-[#00a684] font-semibold bg-[#00a684]/5">Recognized as a high-risk data-processing layer.</td>
              </tr>
            </tbody>
          </table>
        </div>
      `,
    },
    {
      id: "section5",
      title: "Strategic Response: Building an AI-Aware Security Architecture",
      content: `
        <p>To prevent "Trust-based" compromises, organizations must evolve toward an AI-Aware SASE (Secure Access Service Edge) framework. This moves beyond basic connectivity to deep behavioral intelligence.</p>
        
        <h4 class="text-xl font-extrabold text-slate-800 mt-10 mb-2">1. Zero-Trust Identity & Session Integrity</h4>
        <p class="text-slate-500 font-semibold mb-4 text-sm uppercase tracking-wide">Eliminate the "Log in Once, Access Anywhere" vulnerability.</p>
        <ul class="mb-8">
          <li><strong>Contextual Validation:</strong> Every request must verify Device Health (EDR status), IP Reputation, and Geo-velocity before granting access.</li>
          <li><strong>Hardware Binding:</strong> Cryptographically bind sessions to specific device hardware to render exfiltrated cookies/tokens useless on attacker machines.</li>
          <li><strong>Continuous Auth:</strong> Replace one-time MFA with risk-based "step-up" authentication for high-sensitivity actions (e.g., modifying production environment variables).</li>
        </ul>

        <h4 class="text-xl font-extrabold text-slate-800 mt-10 mb-2">2. OAuth & SaaS Governance (SSPM)</h4>
        <p class="text-slate-500 font-semibold mb-4 text-sm uppercase tracking-wide">Control the delegated access surface.</p>
        <ul class="mb-8">
          <li><strong>Inventory & Audit:</strong> Automate discovery of "Shadow AI" and third-party integrations with access to corporate data.</li>
          <li><strong>Scopes of Least Privilege:</strong> Enforce strict limitations on OAuth permissions; block integrations requesting "Global Admin" or broad "Read/Write" access to mail and drives.</li>
          <li><strong>Lifecycle Automation:</strong> Implement auto-revocation for dormant tokens or integrations showing anomalous API spikes.</li>
        </ul>

        <h4 class="text-xl font-extrabold text-slate-800 mt-10 mb-2">3. AI Isolation & Data Scoping</h4>
        <p class="text-slate-500 font-semibold mb-4 text-sm uppercase tracking-wide">Treat AI agents as privileged execution layers, not simple apps.</p>
        <ul class="mb-8">
          <li><strong>Connector Sandboxing:</strong> Isolate AI integrations from CI/CD pipelines and secrets infrastructure to prevent "Prompt Injection" pivots.</li>
          <li><strong>Data Fencing:</strong> Use an AI Gateway to enforce granular data boundaries. AI should access specific datasets, not the entire corporate workspace.</li>
          <li><strong>Action Allow-listing:</strong> Explicitly define which systems an AI can query and what actions it can execute.</li>
        </ul>

        <h4 class="text-xl font-extrabold text-slate-800 mt-10 mb-2">4. Dynamic Secrets Management</h4>
        <p class="text-slate-500 font-semibold mb-4 text-sm uppercase tracking-wide">Move from static credentials to ephemeral, identity-bound access.</p>
        <ul class="mb-8">
          <li><strong>Ephemeral Tokens:</strong> Replace static .env secrets with Just-in-Time (JIT) credentials that expire in minutes.</li>
          <li><strong>Runtime Injection:</strong> Inject secrets directly into memory at runtime; never store plaintext secrets in configuration files or repositories.</li>
          <li><strong>Automated Rotation:</strong> Trigger immediate secret rotation upon any detected behavioral anomaly or suspicious login.</li>
        </ul>

        <h4 class="text-xl font-extrabold text-slate-800 mt-10 mb-2">5. Blast Radius Containment</h4>
        <p class="text-slate-500 font-semibold mb-4 text-sm uppercase tracking-wide">Prevent initial entry from becoming a total compromise.</p>
        <ul class="mb-10">
          <li><strong>Identity Segmentation:</strong> Create "Hard Walls" between SaaS, Dev, and Internal Production environments. Identities should not traverse these zones without re-validation.</li>
          <li><strong>Lateral Suppression:</strong> Use API-level monitoring to detect and block unauthorized cross system communication.</li>
        </ul>

        <div class="overflow-x-auto mt-12 mb-8 rounded-2xl border border-slate-200 shadow-sm">
          <table class="min-w-full text-left border-collapse bg-white">
            <thead class="bg-slate-900 text-white">
              <tr>
                <th class="px-6 py-5 font-black text-[11px] uppercase tracking-widest border-b border-slate-700">Attack Vector</th>
                <th class="px-6 py-5 font-black text-[11px] uppercase tracking-widest border-b border-slate-700 text-slate-400">Legacy Defense</th>
                <th class="px-6 py-5 font-black text-[#00a684] text-[11px] uppercase tracking-widest border-b border-slate-700 bg-slate-800">AI-Aware SASE</th>
              </tr>
            </thead>
            <tbody class="text-slate-600 divide-y divide-slate-100 text-sm font-medium">
              <tr class="hover:bg-slate-50 transition-colors">
                <td class="px-6 py-5 font-bold text-slate-800 border-r border-slate-50">Token Theft</td>
                <td class="px-6 py-5 border-r border-slate-50">Valid until expiration</td>
                <td class="px-6 py-5 text-[#00a684] bg-[#00a684]/5 font-bold">Invalidated (Device/IP Mismatch)</td>
              </tr>
              <tr class="hover:bg-slate-50 transition-colors">
                <td class="px-6 py-5 font-bold text-slate-800 border-r border-slate-50">SaaS Pivot</td>
                <td class="px-6 py-5 border-r border-slate-50">Broad OAuth Access</td>
                <td class="px-6 py-5 text-[#00a684] bg-[#00a684]/5 font-bold">Sandboxed/Scoped Access</td>
              </tr>
              <tr class="hover:bg-slate-50 transition-colors">
                <td class="px-6 py-5 font-bold text-slate-800 border-r border-slate-50">Secrets Exposure</td>
                <td class="px-6 py-5 border-r border-slate-50">Static/Manual Rotation</td>
                <td class="px-6 py-5 text-[#00a684] bg-[#00a684]/5 font-bold">Ephemeral/JIT Injection</td>
              </tr>
              <tr class="hover:bg-slate-50 transition-colors">
                <td class="px-6 py-5 font-bold text-slate-800 border-r border-slate-50">Lateral Movement</td>
                <td class="px-6 py-5 border-r border-slate-50">Open Internal Network</td>
                <td class="px-6 py-5 text-[#00a684] bg-[#00a684]/5 font-bold">Identity-Segmented Zones</td>
              </tr>
            </tbody>
          </table>
        </div>
      `,
    },
    {
      id: "section6",
      title: "The Synthera Perspective",
      content: `
    <div class="mt-12 pt-10 border-t-2 border-emerald-500/30">
      <h3 class="text-2xl font-extrabold text-slate-900 mb-6 tracking-tight">
        From Defense to Resilience
      </h3>

      <p class="text-lg text-slate-700 leading-relaxed mb-6">
        At Synthera, we believe this breach is a definitive turning point. Security is no longer an infrastructure problem; it is a <strong>flow problem</strong>. It’s about controlling how identity, integrations, and data interact in a world where AI is doing the heavy lifting.
      </p>

      <p class="mt-6 text-emerald-600 font-bold text-lg italic">
        Final Thought:
        <span class="text-slate-800 font-medium not-italic">
          Vercel was not "hacked" in the traditional sense; it was compromised through the very trust relationships that make modern development fast. Unless we rethink security around Identity Supply Chains, this won't be an anomaly it will be the standard.
        </span>
      </p>
    </div>
  `,
    },
  ];

  // ADDED: The Table of Contents data array
  const tableOfContents = [
    { id: "section1", title: "1. The Era of 'Inherited' Access" },
    { id: "section2", title: "2. Anatomy of an Identity Pivot" },
    { id: "section3", title: "3. Identity is the New Perimeter" },
    { id: "section4", title: "4. Why Traditional SASE Isn't Enough" },
    { id: "section5", title: "5. Building an AI-Aware Architecture" },
    { id: "section6", title: "6. The Synthera Perspective" },
  ];

  // FIX: Custom Scroll Logic to force FIXED positioning once past the top section
  useEffect(() => {
    const handleScrollFixed = () => {
      if (!sidebarContainerRef.current || !mainGridRef.current) return;

      const containerRect = sidebarContainerRef.current.getBoundingClientRect();
      const gridRect = mainGridRef.current.getBoundingClientRect();

      const asideElement = sidebarContainerRef.current.querySelector("aside");
      const sidebarHeight = asideElement ? asideElement.offsetHeight : 500;

      const offsetTop = 128; // Represents top-32 placement

      // Force the width to perfectly match the grid column even when 'fixed' pulls it out of flow
      setSidebarWidth(sidebarContainerRef.current.offsetWidth);

      // Check if the article is finished (stops sidebar from hitting footer)
      // 96px accounts for the pb-24 padding on the grid
      if (gridRect.bottom <= offsetTop + sidebarHeight + 96) {
        setTocState("bottom");
      }
      // Check if we have scrolled past the top section -> Make it FIXED
      else if (containerRect.top <= offsetTop) {
        setTocState("fixed");
      }
      // Otherwise, it sits normally at the top
      else {
        setTocState("normal");
      }
    };

    window.addEventListener("scroll", handleScrollFixed);
    window.addEventListener("resize", handleScrollFixed);
    handleScrollFixed(); // Run once on load

    return () => {
      window.removeEventListener("scroll", handleScrollFixed);
      window.removeEventListener("resize", handleScrollFixed);
    };
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const handleScroll = () => setShowBackToTop(window.scrollY > 500);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // ADDED: The Intersection Observer to automatically highlight the active section in the TOC while scrolling
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveSection(entry.target.id);
          }
        });
      },
      { rootMargin: "-20% 0px -60% 0px" },
    );

    const sections = document.querySelectorAll("section[id]");
    sections.forEach((section) => observer.observe(section));

    return () => observer.disconnect();
  }, []);

  const handleCopyLink = () => {
    navigator.clipboard.writeText(window.location.href);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <>
      <Helmet>
        <title>
          When Trust Becomes the Attack Surface: Lessons from the Vercel Breach
          in the Age of AI | Synthera Security
        </title>

        <meta
          name="description"
          content="Explore the Vercel breach, AI supply chain attacks, OAuth security risks, identity-based threats, and how organizations can build AI-aware security architectures."
        />

        <meta
          name="keywords"
          content="Vercel Breach, AI Supply Chain Attack, OAuth Security, Identity Security, AI Security, SaaS Security, Zero Trust, Synthera Security"
        />

        <meta name="robots" content="index, follow" />

        <meta
          property="og:title"
          content="When Trust Becomes the Attack Surface: Lessons from the Vercel Breach in the Age of AI"
        />

        <meta
          property="og:description"
          content="Learn how identity-based attacks, OAuth abuse, and AI-powered threats are reshaping modern cybersecurity."
        />

        <meta
          property="og:url"
          content="https://www.syntherasecurity.com/blog/vercel-breach-ai"
        />

        <meta property="og:type" content="article" />

        <link
          rel="canonical"
          href="https://www.syntherasecurity.com/blog/vercel-breach-ai"
        />
      </Helmet>

      <div className="min-h-screen bg-white font-sans text-slate-800 relative">
        {/* Back to Top */}
        <button
          onClick={scrollToTop}
          className={`fixed bottom-8 right-8 z-50 p-4 bg-slate-900 text-white rounded-full shadow-2xl transition-all duration-500 hover:scale-110 hover:bg-[#00a684] ${
            showBackToTop
              ? "opacity-100 translate-y-0"
              : "opacity-0 translate-y-10 pointer-events-none"
          }`}
        >
          <ArrowUp className="w-6 h-6" />
        </button>

        {/* Main Container */}
        <div className="max-w-7xl mx-auto px-6 lg:px-12 pt-32 lg:pt-40">
          <div className="absolute top-0 left-0 right-0 h-20 bg-[#032d28] z-0  rounded-b-[1.5rem]"></div>

          {/* Header Section */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center mb-16">
            <div className="lg:col-span-7">
              <nav className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.2em] mb-8">
                <Link to="/" className="text-[#00a684] no-underline">
                  Home
                </Link>
                <ChevronRight size={12} className="text-gray-300" />
                <Link to="/resources" className="text-[#00a684] no-underline">
                  Resources
                </Link>
                <ChevronRight size={12} className="text-gray-300" />
                <span className="text-[#00a684]">Blog</span>
              </nav>

              <div className="flex items-center gap-6 mb-6">
                <div className="flex items-center gap-2 text-xs font-semibold text-emerald-600 bg-emerald-50 px-3 py-1 rounded-full border border-emerald-100">
                  <Calendar size={14} /> April 2026
                </div>
                <div className="flex items-center gap-2 text-xs font-semibold text-slate-500">
                  <Clock size={14} /> 8m read
                </div>
              </div>

              <h1 className="text-4xl lg:text-[44px] font-extrabold text-slate-900 leading-tight mb-8 tracking-tight">
                When Trust Becomes the Attack Surface: Lessons from the Vercel
                Breach in the Age of AI
              </h1>

              <div className="flex items-center gap-2">
                <div className="w-10 h-10 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-full flex items-center justify-center font-bold text-white shadow-lg text-sm">
                  AS
                </div>

                <div className="leading-tight">
                  <p className="text-base font-bold text-slate-800 mb-0">
                    Akash Sharma
                  </p>

                  <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-[0.12em] mt-0">
                    Senior Security Analyst
                  </p>
                </div>
              </div>
            </div>

            {/* Featured Image Right Side */}
            <div className="lg:col-span-5">
              <div className="relative group">
                {/* Glowing animated border effect */}
                <div className="absolute -inset-1 bg-gradient-to-r from-indigo-500 to-emerald-500 rounded-[2rem] blur opacity-20 transition duration-500 group-hover:opacity-40"></div>

                {/* Image Container */}
                <div className="relative overflow-hidden rounded-[2rem] shadow-2xl bg-slate-900 aspect-[4/3]">
                  <img
                    src="https://res.cloudinary.com/dpn3bkjba/image/upload/f_auto,q_auto,w_1200/v1777029083/Gemini_Generated_Image_ay9ff0ay9ff0ay9f_qcjtgq.png"
                    alt="Vercel Breach AI Security"
                    className="w-full h-full object-cover opacity-90 group-hover:scale-105 transition-transform duration-700"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Content Layout - ADDED ref={mainGridRef} and relative positioning */}
          <div
            ref={mainGridRef}
            className="grid grid-cols-1 lg:grid-cols-12 gap-12 pb-24 items-start relative"
          >
            {/* FIXED LEFT SIDEBAR HANDLED BY JS */}
            <div
              ref={sidebarContainerRef}
              className="lg:col-span-3 hidden lg:block relative h-full"
            >
              <aside
                style={{
                  width: tocState !== "normal" ? `${sidebarWidth}px` : "auto",
                }}
                className={`
                ${tocState === "fixed" ? "fixed top-32" : ""}
                ${tocState === "bottom" ? "absolute bottom-24" : ""}
                ${tocState === "normal" ? "relative" : ""}
                space-y-8 max-h-[calc(100vh-10rem)] overflow-y-auto no-scrollbar
              `}
              >
                {/* Table of Contents UI block */}
                <div className="bg-white rounded-2xl border border-slate-100 p-6 shadow-sm mb-8">
                  <h4 className="text-[11px] font-black uppercase tracking-[0.3em] mb-6 text-slate-400 border-b pb-4">
                    Table of Contents
                  </h4>
                  <ul className="space-y-4">
                    {tableOfContents.map((item) => {
                      const isActive = activeSection === item.id;
                      return (
                        <li key={item.id} className="toc-item">
                          <a
                            href={`#${item.id}`}
                            className={`text-sm font-bold leading-snug block no-underline transition-all ${
                              isActive
                                ? "text-emerald-600 translate-x-1"
                                : "text-slate-500 hover:text-slate-800"
                            }`}
                          >
                            {item.title}
                          </a>
                        </li>
                      );
                    })}
                  </ul>
                </div>

                <div className="bg-white rounded-2xl border border-slate-100 p-6 shadow-sm">
                  <p className="text-[11px] font-black uppercase tracking-[0.3em] mb-4 text-slate-400">
                    Share Insight
                  </p>
                  <div className="flex gap-3">
                    <button className="flex-1 p-3 bg-slate-50 rounded-xl hover:bg-[#1DA1F2]/10 hover:text-[#1DA1F2] transition-all">
                      <Twitter size={18} className="mx-auto" />
                    </button>
                    <a
                      href="https://www.linkedin.com/feed/update/urn:li:activity:7453415504721125376"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex-1 p-3 bg-slate-50 rounded-xl hover:bg-emerald-50 hover:text-emerald-600 transition-all"
                    >
                      <Linkedin size={18} className="mx-auto" />
                    </a>
                    <button
                      onClick={handleCopyLink}
                      className="flex-1 p-3 bg-slate-50 rounded-xl hover:bg-emerald-50 hover:text-emerald-600 transition-all"
                    >
                      {copied ? (
                        <Check size={18} className="mx-auto" />
                      ) : (
                        <Copy size={18} className="mx-auto" />
                      )}
                    </button>
                  </div>
                </div>
              </aside>
            </div>

            {/* MAIN ARTICLE AREA */}
            <div className="lg:col-span-9 max-w-3xl" ref={contentRef}>
              <div className="prose prose-lg max-w-none text-slate-600 leading-relaxed blog-content">
                {/* Dynamic Content Sections */}
                {articleSections.map((section) => (
                  <section
                    key={section.id}
                    id={section.id}
                    className="mb-16 pt-8 border-t border-slate-100 first:border-0 first:pt-0"
                  >
                    <h2 className="text-3xl font-bold text-slate-800 m-0 mb-8 leading-tight">
                      {section.title}
                    </h2>
                    <div
                      dangerouslySetInnerHTML={{ __html: section.content }}
                    />
                  </section>
                ))}
              </div>
            </div>
          </div>
        </div>
        <Footer />
        <style>{`
        .blog-content h2 { margin-top: 0; color: #0f172a; font-weight: 800; }
        .blog-content p { margin-bottom: 1.75rem; font-size: 1.125rem; line-height: 1.85; }
        
        .blog-content section ul { list-style-type: none; padding-left: 0; margin-bottom: 2rem; }
        .blog-content section li { position: relative; padding-left: 1.75rem; margin-bottom: 0.75rem; font-weight: 500; }
        .blog-content section li::before {
          content: "•";
          color: #10b981;
          font-weight: bold;
          position: absolute;
          left: 0;
          font-size: 1.5rem;
          line-height: 1.2;
        }

        .toc-item::before, .dynamic-list li::before { display: none !important; }

        .highlight-box {
          background: #f0faf8;
          border-left: 4px solid #10b981;
          padding: 2rem;
          border-radius: 0 1.5rem 1.5rem 0;
          margin: 2.5rem 0;
          color: #064e3b;
        }
        .insight-box {
          background: #0f172a;
          color: white;
          padding: 2.5rem;
          border-radius: 1.5rem;
          margin: 3rem 0;
        }
        .insight-box h4 { color: #10b981; margin-bottom: 1rem; font-weight: 800; }
        html { scroll-behavior: smooth; }

        .no-scrollbar::-webkit-scrollbar {
          display: none;
        }
        .no-scrollbar {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
      `}</style>
      </div>
    </>
  );
};

export default VercelBlog;
